/**
 * createAudioGizmoController — standalone factory for the audio gizmo controller.
 *
 * Mirrors the {@link createGizmoController} pattern: dynamic import so the
 * billboard/material code is tree-shaken out of bundles whose consumers
 * never call the factory.
 *
 * Lifecycle — consumer responsibilities:
 *   - Call after the viewer has a scene (await `viewerEl.whenReady()`).
 *   - Call `controller.dispose()` before the Babylon scene is torn down.
 *   - Call `controller.setVisible(false)` on entering play mode so the
 *     editor-only markers don't appear in previews.
 *   - Call `controller.rescan()` after large scene loads if your editor
 *     doesn't already dispatch `editor:resources-changed` — the controller
 *     listens for that event by default.
 */

import type { TraceViewer } from "../../core/TraceViewer";
import type { AudioGizmoController } from "./AudioGizmoController";

export async function createAudioGizmoController(
    viewer: TraceViewer,
): Promise<AudioGizmoController | null> {
    const babylonScene = viewer.scene;
    const traceScene = viewer.traceScene;
    if (!babylonScene || !traceScene) return null;
    const { AudioGizmoController } = await import("./AudioGizmoController");
    return new AudioGizmoController(babylonScene, traceScene);
}
